import Digest from "../util/digest";

import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { ApiOpts, IEventChannel } from "../model/Models";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class GeomModule
 */
export default class GeomModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    private lastBufferKey;
    private lastBufferPromise; // We cache the results of the last buffer operations because a lot of them were being repeated, and repeat trips to the server are inefficient

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.extractPoints = this.extractPoints.bind(this);
        this.extractMidPoints = this.extractMidPoints.bind(this);
        this.extractLines = this.extractLines.bind(this);
        this.transform = this.transform.bind(this);
        this.getBounds = this.getBounds.bind(this);
        this.generalize = this.generalize.bind(this);
        this.buffer = this.buffer.bind(this);
        this.convexHull = this.convexHull.bind(this);
        this.union = this.union.bind(this);
        this.intersection = this.intersection.bind(this);
        this.symDifference = this.symDifference.bind(this);
        this.difference = this.difference.bind(this);
        this.getType = this.getType.bind(this);
        this.sanitize = this.sanitize.bind(this);
    }

    /**
     * Get all unique points from the geometry given and pass them to the callback given
     * @param geom the geometry
     * @param callback callback function
     * @param {optional} owner - this for the callback function
     * 
     * @returns
     */
    extractPoints(geom, callback: Function, owner?: any) {
        this.getType(geom).extractPoints(geom, callback, owner ? owner : window);
    }

    /**
     * Get all mid points from the geometry given and pass them to the callback given
     * 
     * @param geom the geometry
     * @param callback callback function
     * @param {optional} owner - this for the callback function
     * 
     * @returns
     */
    extractMidPoints(geom, callback: Function, owner?: any) {
        const type = this.getType(geom);

        if (type.extractMidPoints) {
            type.extractMidPoints(geom, callback, owner ? owner : window);
        } else {
            callback.call(owner, []); // return empty array
        }
    }
    
    /**
     * Get all unique lines from the geometry given and pass them to the callback given
     * 
     * @param geom the geometry
     * @param callback callback function
     * @param {optional} owner - this for the callback function
     * 
     * @returns
     */
    extractLines(geom, callback: Function, owner?: any) {
        this.getType(geom).extractLines(geom, callback, owner ? owner : window);
    }
    
    /**
     * 
     * @param geom 
     * @param matrix 
     * 
     * @returns 
     */
    transform(geom, matrix) {
        return this.getType(geom).transform(geom, matrix);
    }
    
    /**
     * 
     * @param geom 
     * @param bounds 
     * 
     * @returns 
     */
    getBounds(geom, bounds?: number[]) {
        if (!bounds) {
            bounds = [Number.MAX_VALUE, Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE];
        }

        return this.getType(geom).getBounds(geom, bounds);
    }
    
    generalize(geom, threshold?: number) {
        if (!threshold) {
            threshold = 1;
        }

        return this.getType(geom).generalize(geom, threshold);
    }
    
    /**
     * Buffer the geometry given by the distance given. return a deferred object which will be resolved with the buffered geometry
     * 
     * @param geom 
     * @param srid 
     * @param distance 
     * @param unit 
     * @param opts 
     * 
     * @returns 
     */
    buffer(geom, srid, distance, unit, opts?: ApiOpts) {
        const key = new Digest().update(geom).update(srid).update(distance).update(unit).update(opts).finish();

        if (key !== this.lastBufferKey) {
            this.lastBufferKey = key;
            this.lastBufferPromise = this.rqBroker.doPost({
                url: es.restUrl + "/geom/buffer",
                data : "geom=" + es.util.url.stringify(geom) + "&srid=" + srid + "&unit=" + unit + "&distance=" + distance,
                contentType : "application/x-www-form-urlencoded; charset=UTF-8",
                opts: opts
            });
        }

        return this.lastBufferPromise;
    }
    
    /**
     * 
     * @param geom 
     * @param opts 
     * 
     * @returns 
     */
    convexHull(geom, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/convexHull" + "?geom=" + es.util.url.stringify(geom),
            opts: opts
        });
    }
    
    /**
     * 
     * @param geoms 
     * @param tolerance 
     * @param opts 
     * 
     * @returns 
     */
    union(geoms, tolerance?: number, opts?: ApiOpts) {
        if (tolerance == null) {
            tolerance = 0;
        }

        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/union",
            contentType : 'application/x-www-form-urlencoded',
            data: "geoms="+es.util.url.stringify(geoms)+"&tolerance="+tolerance,
            opts: opts
        });
    }
    
    /**
     * 
     * @param a 
     * @param b 
     * @param opts 
     * 
     * @returns 
     */
    intersection(a, b, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/intersection" + "?a=" + es.util.url.stringify(a) + "b=" + es.util.url.stringify(b),
            opts: opts
        });
    }
    
    /**
     * 
     * @param a 
     * @param b 
     * @param opts 
     * 
     * @returns 
     */
    symDifference(a, b, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/sym_difference" + "?a=" + es.util.url.stringify(a) + "b=" + es.util.url.stringify(b),
            opts: opts
        });
    }
    
    /**
     * 
     * @param a 
     * @param b 
     * @param opts 
     * 
     * @returns 
     */
    difference(a, b, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/difference" + "?a=" + es.util.url.stringify(a) + "b=" + es.util.url.stringify(b),
            opts: opts
        });
    }
    
    private getType(geom) {
        const type = es.util.geom.types[geom[0]];
        if (!type) {
            throw new Error("Unknown geometry type : " + geom[0]);
        }

        return type;
    }

    /**
     * Geometry to sanitize
     * @param geom
     * @param opts
     */
    sanitize(geom, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            url: es.restUrl + "/geom/sanitize",
            data: "geom=" + es.util.url.stringify(geom),
            contentType: "application/x-www-form-urlencoded; charset=UTF-8",
            opts: opts
        });
    };
}
